/*
 * Created Date: Tue, 4th May 2021, 14:38:47 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2021 The Distance
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import {
  CheckCircle as ActionCheck,
  ErrorOutline as AlertError,
} from '@material-ui/icons';
import classnames from 'classnames';
import { useTranslate } from 'react-admin';

const useStyles = makeStyles(
  theme => ({
    contentText: {
      minWidth: 400,
      textAlign: 'center',
    },
    confirmPrimary: {
      color: theme.palette.primary.main,
    },
    confirmWarning: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: fade(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    iconPaddingStyle: {
      paddingRight: '0.5em',
    },
  }),
  { name: 'RaConfirm' }
);

const Confirm = props => {
  const {
    isOpen,
    loading,
    title,
    content,
    confirm,
    cancel,
    confirmColor,
    ConfirmIcon,
    CancelIcon,
    onClose,
    onConfirm,
    hideConfirm = false,
    translateOptions = {},
  } = props;
  const classes = useStyles(props);
  const translate = useTranslate();

  const handleConfirm = useCallback(
    e => {
      e.stopPropagation();
      onConfirm(e);
    },
    [onConfirm]
  );

  const handleClick = useCallback(e => {
    e.stopPropagation();
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onClick={handleClick}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">
        {typeof title === 'function' && title !== null
          ? title
          : translate(title, { _: title, ...translateOptions })}
      </DialogTitle>
      <DialogContent className={classes.contentText}>{content}</DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          <CancelIcon className={classes.iconPaddingStyle} />
          {translate(cancel, { _: cancel })}
        </Button>
        {hideConfirm ? null : (
          <Button
            disabled={loading}
            onClick={handleConfirm}
            className={classnames('ra-confirm', {
              [classes.confirmWarning]: confirmColor === 'warning',
              [classes.confirmPrimary]: confirmColor === 'primary',
            })}
            autoFocus
          >
            <ConfirmIcon className={classes.iconPaddingStyle} />
            {translate(confirm, { _: confirm })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

Confirm.propTypes = {
  cancel: PropTypes.string,
  classes: PropTypes.object,
  confirm: PropTypes.string,
  confirmColor: PropTypes.string,
  ConfirmIcon: PropTypes.elementType,
  CancelIcon: PropTypes.elementType,
  content: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.func.isRequired,
  ]),
};

Confirm.defaultProps = {
  cancel: 'ra.action.cancel',
  classes: {},
  confirm: 'ra.action.confirm',
  confirmColor: 'primary',
  ConfirmIcon: ActionCheck,
  CancelIcon: AlertError,
  isOpen: false,
};

export default Confirm;
